import { ntxAPI } from '@API/axiosNeatex';
import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { APIResponse } from '@store/Shared/shared';

// ====== Types ======

export interface Filter {
  field: string;
  operator: number;
  value: string | number | null;
}

export interface SortOrder {
  field: string;
  descending: boolean;
}

/**
 * حالة جدول واحد (tableKey واحد)
 */
export interface TableState {
  data: any[];
  totalItems: number;
  filters: Filter[];
  sortOrders: SortOrder[];
  pageIndex: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  endpoint: string;

  // خصائص إضافية للجسم (body)
  includes?: any | null; // لو احتجت Includes من السيرفر
  extraFilters?: any | null; // لو عندك ExtraFilters حسب الـ Backend
  fixedFilters?: Record<string, any>; // الفلاتر الثابتة (itemCode, name, ...)

  // باراميترات في الـ URL (query string) مثل PriceListId
  queryParams?: Record<string, any>;
}

/**
 * خريطة الجداول الديناميكية:
 * dynamicTable["InvMasterItems"], dynamicTable["PriceListItemsIn"], ...
 */
export interface DynamicTableState {
  [tableKey: string]: TableState | undefined;
}

// حالة افتراضية لكل جدول
const createInitialTableState = (): TableState => ({
  data: [],
  totalItems: 0,
  filters: [],
  sortOrders: [],
  pageIndex: 1,
  pageSize: 10,
  loading: false,
  error: null,
  endpoint: '',
  includes: null,
  extraFilters: null,
  fixedFilters: {},
  queryParams: undefined
});

const initialState: DynamicTableState = {};

// helper لضمان وجود حالة للـ tableKey
const ensureTableState = (state: DynamicTableState, tableKey: string): TableState => {
  if (!state[tableKey]) {
    state[tableKey] = createInitialTableState();
  }
  return state[tableKey] as TableState;
};

type PagedDataResponse = {
  totalItems: number;
  data: any[];
  pageIndex: number;
  pageSize: number;
};

interface GetPagedDataArgs {
  tableKey: string;
  // لو حبيت تمرّر Fixed Filters مؤقتة مع هذا النداء فقط
  fixedFiltersOverride?: Record<string, any>;
}

// 🧠 thunk عام لجلب البيانات لأي جدول / رابط
export const getPagedData = createAsyncThunk(
  'dynamicTable/getPagedData',
  async ({ tableKey, fixedFiltersOverride }: GetPagedDataArgs, thunkAPI) => {
    const rootState = thunkAPI.getState() as { dynamicTable: DynamicTableState };
    const tableState = rootState.dynamicTable[tableKey];

    if (!tableState || !tableState.endpoint) {
      return thunkAPI.rejectWithValue('Endpoint is not set for this table.');
    }

    const { endpoint, filters, sortOrders, pageIndex, pageSize, includes, extraFilters, fixedFilters, queryParams } =
      tableState;

    // ندمج الفلاتر الثابتة: اللي في الـ state + اللي جت Override مع النداء
    const finalFixedFilters = {
      ...fixedFilters,
      ...(fixedFiltersOverride || {})
    };

    // الجسم النهائي للريكوست
    const requestBody: any = {
      pageIndex,
      pageSize,
      filters,
      sortOrders,
      includes: includes ?? null,
      extraFilters: extraFilters ?? null,
      ...finalFixedFilters // هنا يطلع معك itemCode, name, categoryId, ... الفلاتر الثابتة
    };

    try {
      const { data } = await ntxAPI.post<APIResponse<PagedDataResponse>>(endpoint, requestBody, {
        params: queryParams // مثل PriceListId
      });

      if (data.succeeded === true) {
        // نرجع tableKey عشان نعرف لأي جدول ننزل الداتا
        return { tableKey, ...data.data };
      } else {
        return thunkAPI.rejectWithValue(data.message);
      }
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data || 'Fetch failed');
    }
  }
);

const dynamicTableSlice = createSlice({
  name: 'dynamicTable',
  initialState,
  reducers: {
    setEndpoint: (state, action: PayloadAction<{ tableKey: string; endpoint: string }>) => {
      const { tableKey, endpoint } = action.payload;
      const table = ensureTableState(state, tableKey);
      table.endpoint = endpoint;
    },
    setFilters: (state, action: PayloadAction<{ tableKey: string; filters: Filter[] }>) => {
      const { tableKey, filters } = action.payload;
      const table = ensureTableState(state, tableKey);
      table.filters = filters;
      table.pageIndex = 1; // إعادة أول صفحة عند تغيير الفلاتر
    },
    setSortOrders: (state, action: PayloadAction<{ tableKey: string; sortOrders: SortOrder[] }>) => {
      const { tableKey, sortOrders } = action.payload;
      const table = ensureTableState(state, tableKey);
      table.sortOrders = sortOrders;
      table.pageIndex = 1;
    },
    setPagination: (state, action: PayloadAction<{ tableKey: string; pageIndex: number; pageSize: number }>) => {
      const { tableKey, pageIndex, pageSize } = action.payload;
      const table = ensureTableState(state, tableKey);
      table.pageIndex = pageIndex;
      table.pageSize = pageSize;
    },
    setIncludes: (state, action: PayloadAction<{ tableKey: string; includes: any }>) => {
      const { tableKey, includes } = action.payload;
      const table = ensureTableState(state, tableKey);
      table.includes = includes;
    },
    setExtraFilters: (state, action: PayloadAction<{ tableKey: string; extraFilters: any }>) => {
      const { tableKey, extraFilters } = action.payload;
      const table = ensureTableState(state, tableKey);
      table.extraFilters = extraFilters;
    },
    setFixedFilters: (state, action: PayloadAction<{ tableKey: string; fixedFilters: Record<string, any> }>) => {
      const { tableKey, fixedFilters } = action.payload;
      const table = ensureTableState(state, tableKey);
      table.fixedFilters = fixedFilters;
      table.pageIndex = 1;
    },
    setQueryParams: (state, action: PayloadAction<{ tableKey: string; queryParams: Record<string, any> }>) => {
      const { tableKey, queryParams } = action.payload;
      const table = ensureTableState(state, tableKey);
      table.queryParams = queryParams;
    },

    clearTableData: (state, action: PayloadAction<{ tableKey: string }>) => {
      const { tableKey } = action.payload;
      const table = ensureTableState(state, tableKey);

      // ✅ تصفية النتائج فقط (مع الحفاظ على الإعدادات)
      table.data = [];
      table.totalItems = 0;
      table.loading = false;
      table.error = null;

      // اختياري: رجّع لأول صفحة
      table.pageIndex = 1;
    },
    resetTableState: (state, action: PayloadAction<{ tableKey: string }>) => {
      const { tableKey } = action.payload;

      // ✅ إعادة ضبط الجدول بالكامل للوضع الافتراضي
      state[tableKey] = createInitialTableState();
    },
    removeTableState: (state, action: PayloadAction<{ tableKey: string }>) => {
      const { tableKey } = action.payload;
      // ✅ حذف الجدول نهائيًا من الـ store
      delete state[tableKey];
    },
    clearAllTables: state => {
      // ✅ تنظيف كل الجداول
      Object.keys(state).forEach(k => delete state[k]);
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getPagedData.pending, (state, action) => {
        const tableKey = action.meta.arg.tableKey;
        const table = ensureTableState(state, tableKey);
        table.loading = true;
        table.error = null;
      })
      .addCase(getPagedData.fulfilled, (state, action) => {
        const { tableKey, data, totalItems, pageIndex, pageSize } = action.payload as PagedDataResponse & {
          tableKey: string;
        };
        const table = ensureTableState(state, tableKey);
        table.loading = false;
        table.data = data || [];
        table.totalItems = totalItems || 0;
        table.pageIndex = pageIndex;
        table.pageSize = pageSize;
      })
      .addCase(getPagedData.rejected, (state, action) => {
        const tableKey = (action.meta as any).arg?.tableKey as string | undefined;
        if (!tableKey) return;
        const table = ensureTableState(state, tableKey);
        table.loading = false;
        table.error = (action.payload as string) ?? 'Fetch failed';
      });
  }
});

export const {
  setEndpoint,
  setFilters,
  setSortOrders,
  setPagination,
  setIncludes,
  setExtraFilters,
  setFixedFilters,
  setQueryParams,

  // ✅ الجديد
  clearTableData,
  resetTableState,
  removeTableState,
  clearAllTables
} = dynamicTableSlice.actions;

export default dynamicTableSlice.reducer;
